import React, { useState, useEffect } from "react";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CForm,
  CFormLabel,
  CFormInput,
  CButton,
  CRow,
  CCol,
} from "@coreui/react";
import { getUplines } from "src/services/Stores";
import { getSalesList, getProvincesBySMD, getCitiesBySMDAndProvince } from "src/services/SMD";
import Select from "react-select"

const StoreFilter = ({ open, onClose, onApply, initialFilters }) => {
  const [filters, setFilters] = useState(
    initialFilters || {
      name: "",
      type: "",
      smdId: "",
      status: "",
      daysSinceVisit: "",
      upline_name: "",
      provinceId: "",
      cityId: "",
      visitStatus: "",
    }
  );
  const [uplines, setUplines] = useState([]);
  const [loadingUplines, setLoadingUplines] = useState(false);
  const [smdOptions, setSmdOptions] = useState([]);
  const [loadingSMD, setLoadingSMD] = useState(false);
  const [smd, setSmd] = useState(0);
  const [provOptions, setProvOptions] = useState([]);
  const [prov, setProv] = useState(0);
  const [cityOptions, setCityOptions] = useState([]);

  // Fetch uplines for the dropdown
  useEffect(() => {
    const fetchUplines = async () => {
      setLoadingUplines(true);
      try {
        const response = await getUplines();
        const uplinesData = response?.data || response?.uplines || response || [];
        const options = uplinesData.map((up) => ({
          value: up.id,
          label: up.name
        }))
        const data = [{ value: "", label: "Semua Upline"}, { value: "no_upline", label: "Tanpa Upline"}, ...options]
        setUplines(data);
      } catch (error) {
        console.error("Error fetching uplines:", error);
        setUplines([]);
      } finally {
        setLoadingUplines(false);
      }
    };

    if (open) {
      fetchUplines();
    }
  }, [open]);

  // Fetch SMD options for the dropdown
  useEffect(() => {
    const fetchSMDOptions = async () => {
      setLoadingSMD(true);
      try {
        const response = await getSalesList();
        const smdData = response?.data || response || [];
        const options = smdData.map((smd) => ({
          value: smd.id,
          label: `${smd.name} - ${smd.id}`,
        }));
        const data = [{ value: "", label: "Semua SMD"}, ...options];
        setSmdOptions(data);
      } catch (error) {
        console.error("Error fetching SMD options:", error);
        setSmdOptions([]);
      } finally {
        setLoadingSMD(false);
      }
    };

    if (open) {
      fetchSMDOptions();
    }
  }, [open]);

  useEffect(() => {
    const fetchProvOptions = async (smd) => {
      try {
        const response = await getProvincesBySMD(smd);
        const provData = response?.data || response || [];
        const options = provData.map((prov) => ({
          value: prov.province_id,
          label: prov.province_name,
        }));
        const data = [{ value: null, label: "Semua Provinsi"}, ...options];
        setProvOptions(data);
      } catch (error) {
        console.error("Error fetching Prov options:", error);
        setProvOptions([]);
      }
    };

    if(smd !== "0") fetchProvOptions(smd)
  }, [smd])

  useEffect(() => {
    const fetchCityOptions = async (smd, prov) => {
      try {
        const response = await getCitiesBySMDAndProvince(smd, prov);
        const cityData = response?.data || response || [];
        const options = cityData.map((city) => ({
          value: city.city_id,
          label: city.city_name,
        }));
        const data = [{ value: null, label: "Semua Kota"}, ...options]
        setCityOptions(data);
      } catch (error) {
        console.error("Error fetching City options:", error);
        setCityOptions([]);
      }
    };

    if(prov !== "0") fetchCityOptions(smd, prov)
  }, [prov])

  const typeOptions = [
    { value: "", label: "Semua Tipe"},
    { value: "A", label: "A"},
    { value: "B", label: "B"},
    { value: "C", label: "C"},
    { value: "D", label: "D"}
  ]

  const daysSinceVisitOptions = [
    { value: "", label: "Semua"},
    { value: "7", label: "< 7 hari"},
    { value: "14", label: "7-14 hari"},
    { value: "30", label: "15-30 hari"},
    { value: "31", label: "> 30 hari"}
  ]

  const visitStatusOptions = [
    { value: "", label: "Semua Status"},
    { value: "Terkunjungi", label: "Terkunjungi"},
    { value: "Belum Terkunjungi", label: "Belum Terkunjungi"}
  ]

  const handleInputChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onApply(filters);
  };

  const handleReset = () => {
    setFilters({
      name: "",
      type: "",
      smdId: "",
      status: "",
      daysSinceVisit: "",
      upline_name: "",
      provinceId: "",
      cityId: "",
      visitStatus: "",
    });
    onApply({}); // Apply empty filters to reset
  };

  return (
    <CModal visible={open} onClose={onClose} size="lg">
      <CModalHeader>
        <CModalTitle>Filter Toko</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <CForm onSubmit={handleSubmit}>
          <CRow className="mb-3">
            <CCol md={6}>
              <CFormLabel>Nama Toko</CFormLabel>
              <CFormInput
                type="text"
                name="name"
                value={filters.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="Cari berdasarkan nama"
              />
            </CCol>
            <CCol md={6}>
              <CFormLabel>Tipe Toko</CFormLabel>
              <Select 
                name="type" 
                value={typeOptions.find(opt => opt.value === filters.type)} 
                onChange={(e) => handleInputChange("type", e.value)}
                options={typeOptions}
              />
            </CCol>
          </CRow>

          <CRow className="mb-3">
            <CCol md={6}>
              <CFormLabel>SMD</CFormLabel>
              <Select 
                name="smdId" 
                value={smdOptions.find(opt => opt.value === filters.smdId)} 
                onChange={(e) => {handleInputChange("smdId", e.value); setSmd(e.value)}}
                disabled={loadingSMD}
                options={smdOptions}
              />
            </CCol>
            <CCol md={6}>
              <CFormLabel>Upline</CFormLabel>
              <Select 
                name="upline_name" 
                value={uplines.find(opt => opt.value === filters.upline_name)} 
                onChange={(e) => handleInputChange("upline_name", e.value)}
                disabled={loadingUplines}
                options={uplines}
              />
            </CCol>
          </CRow>

          <CRow className="mb-3">
            <CCol md={6}>
              <CFormLabel>Provinsi</CFormLabel>
              <Select 
                name="provinceId" 
                value={provOptions.find( opt => opt.value === filters.provinceId)} 
                onChange={(e) => {handleInputChange("provinceId", e.value ); setProv(e.value)}}
                options={provOptions}
              />
            </CCol>
            <CCol md={6}>
              <CFormLabel>Kota</CFormLabel>
              <Select 
                name="cityId" 
                value={cityOptions.find(opt => opt.value === filters.cityId)} 
                onChange={(e) => handleInputChange("cityId", e.value)}
                disabled={prov == "0"}
                options={cityOptions}
              />
            </CCol>
          </CRow>

          <CRow className="mb-3">
            <CCol md={6}>
              <CFormLabel>Hari Sejak Kunjungan Terakhir</CFormLabel>
              <Select 
                name="daysSinceVisit" 
                value={daysSinceVisitOptions.find(opt => opt.value === filters.daysSinceVisit)} 
                onChange={(e) => handleInputChange("daysSinceVisit", e.value)}
                options={daysSinceVisitOptions}
              />
            </CCol>
            <CCol md={6}>
              <CFormLabel>Status Kunjungan</CFormLabel>
              <Select 
                name="visitStatus" 
                value={visitStatusOptions.find(opt => opt.value === filters.visitStatus)} 
                onChange={(e) => handleInputChange("visitStatus", e.value)}
                options={visitStatusOptions}
              />
            </CCol>
          </CRow>
        </CForm>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={handleReset}>
          Reset
        </CButton>
        <CButton color="primary" onClick={handleSubmit}>
          Terapkan Filter
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default StoreFilter;
